import gql from 'graphql-tag';
import { OwnUserResponse, StreamChat } from 'stream-chat';
import { connect, StreamFeed } from 'getstream';
import AppConfig from '@config/AppConfig';
import { fetchFeedsToken, fetchStreamToken } from '@api/StreamTokenApi';
import DatadogService from '@services/DatadogService';

const CHAT_OPTIONS = { timeout: 15000 };
// Fetch a new Feeds token this long before the current one expires
const FEEDS_TOKEN_REFRESH_LEAD_MS = 5 * 60 * 1000;
// Retry a failed Feeds token refresh after this long; also the shortest wait between refreshes
const FEEDS_TOKEN_RETRY_MS = 30 * 1000;

export interface GqlClient {
  mutate: <T = unknown>(options: { mutation: ReturnType<typeof gql>; variables?: Record<string, unknown> }) => Promise<{ data?: T | null }>;
  query: (options: { query: ReturnType<typeof gql> }) => Promise<{ data: { streamChatToken: string } }>;
}

interface StreamProvider {
  setState: (state: { streamChatToken?: string; streamClient?: StreamChat }) => void;
}

interface StreamUser {
  id: number | string;
  name: string;
}

interface NotificationsUser {
  id?: number | string;
  stream_notifications_id?: string;
}

interface NotificationsCounts {
  realtimeEventCount: number;
  unseenCount: number;
}

interface NotificationsProviderState {
  setState: (
    state: ((previous: NotificationsCounts) => NotificationsCounts) | { streamNotificationsFeed: StreamFeed }
  ) => void;
}

// passing in App component for updating state.
// Every user gets the revocable REST token (arkestro-stream-api) via a tokenProvider the Stream SDK
// re-invokes on expiry; the legacy GraphQL streamChatToken path has been retired now that v2 is GA.
export const setupStream = async (
  provider: StreamProvider,
  user: StreamUser
): Promise<StreamChat> => {
  const chatClient = new StreamChat(AppConfig.streamKey as string, CHAT_OPTIONS);

  const tokenProvider = async (): Promise<string> => {
    const { streamtoken } = await fetchStreamToken();
    return streamtoken;
  };

  await chatClient.connectUser({
    id: user.id.toString(),
    name: user.name,
    privacy_settings: {
      read_receipts: {
        enabled: true
      }
    }
  } as OwnUserResponse, tokenProvider);

  provider.setState({ streamClient: chatClient });

  return chatClient;
};

/**
 * Fetches an expiring Feeds token, creates the streamNotificationsFeed object for this user, sets it on the
 * provider's state, and subscribes to real-time updates. Each real-time event bumps realtimeEventCount (which the
 * bell uses to refetch) and unseenCount. Before the token expires, fetches a new one and swaps in a new feed.
 * A failed token fetch, including the first one, is retried. Timers don't advance while the machine sleeps, so
 * the refresh also runs as soon as the tab becomes visible or comes back online once it is due.
 * Skipped when the backend returns no Feeds token, which is how it signals Feeds is disabled for this environment.
 * @param provider Provider whose state holds the feed and counts
 * @param user Current user from the auth payload
 * @returns A function that stops refreshing and closes the subscription, or undefined when Feeds isn't set up
 */
export const setupStreamNotifications = async (
  provider: NotificationsProviderState,
  user: NotificationsUser
): Promise<(() => void) | undefined> => {
  // User may not exist when on deeplink
  if (!user?.id || !user.stream_notifications_id) return undefined;
  if (!AppConfig.streamNotificationsKey || !AppConfig.streamNotificationsAppID) return undefined;

  const feedId = user.stream_notifications_id;
  let userStreamClient: ReturnType<typeof connect> | undefined;
  let feed: StreamFeed | undefined;
  let refreshTimer: ReturnType<typeof setTimeout> | undefined;
  // Date.now() reading at which the current token is due for a refresh; unset while a fetch is pending
  let refreshDueAt: number | undefined;
  let stopped = false;

  const openFeed = (feedToken: string) => {
    userStreamClient ??= connect(
      AppConfig.streamNotificationsKey as string,
      null, // No secret objects on client side, would use this for server-side NodeJS
      AppConfig.streamNotificationsAppID
    );
    const nextFeed = userStreamClient.feed('notification', feedId, feedToken);

    provider.setState({ streamNotificationsFeed: nextFeed });

    nextFeed
      .subscribe(() => {
        provider.setState(previous => ({
          realtimeEventCount: previous.realtimeEventCount + 1,
          unseenCount: previous.unseenCount + 1,
        }));
      })
      .catch(error => {
        DatadogService.captureErrorMessage('GetStream notifications subscription failed', { error });
      });

    return nextFeed;
  };

  // Counts from now using the token's lifetime, not the server's expiry time, so a skewed client clock
  // can't make the refresh fire late or repeatedly
  const untilRefresh = (tokenExpiresInSeconds: number) => (
    tokenExpiresInSeconds * 1000 - FEEDS_TOKEN_REFRESH_LEAD_MS
  );

  // Fetches a token and swaps in a feed for it. Resolves 'disabled' when the backend has Feeds turned off;
  // otherwise schedules the next refresh, or a retry if the fetch failed.
  const loadToken = async (): Promise<'active' | 'disabled'> => {
    const runAfter = (delayMs: number) => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        loadToken().catch(error => {
          DatadogService.captureErrorMessage('GetStream notifications feed setup failed', { error });
        });
      }, Math.max(delayMs, FEEDS_TOKEN_RETRY_MS));
    };
    const failed = (details: Record<string, unknown>) => {
      DatadogService.captureErrorMessage('GetStream notifications token fetch failed', details);
      runAfter(FEEDS_TOKEN_RETRY_MS);
      return 'active' as const;
    };

    refreshDueAt = undefined;

    let response;
    try {
      response = await fetchFeedsToken();
    } catch (error) {
      return stopped ? 'active' : failed({ error });
    }
    if (stopped) return 'active';

    const { errors, expiresIn, token } = response;
    if (!token) {
      // No errors means Feeds is turned off; keep any current feed until it expires
      return errors ? failed({ errors }) : 'disabled';
    }

    // The client keys subscriptions by channel, so the old feed must unsubscribe before the new one
    // subscribes, or its unsubscribe would cancel the new subscription
    feed?.unsubscribe();
    feed = openFeed(token);
    if (expiresIn) {
      refreshDueAt = Date.now() + untilRefresh(expiresIn);
      runAfter(untilRefresh(expiresIn));
    }
    return 'active';
  };

  const refreshIfDue = () => {
    if (stopped || refreshDueAt === undefined || Date.now() < refreshDueAt) return;
    if (document.visibilityState === 'hidden') return;

    clearTimeout(refreshTimer);
    loadToken().catch(error => {
      DatadogService.captureErrorMessage('GetStream notifications feed setup failed', { error });
    });
  };

  try {
    if (await loadToken() === 'disabled') return undefined;
  } catch (error) {
    DatadogService.captureErrorMessage('GetStream notifications feed setup failed', { error });
    return undefined;
  }

  document.addEventListener('visibilitychange', refreshIfDue);
  window.addEventListener('online', refreshIfDue);

  return () => {
    stopped = true;
    clearTimeout(refreshTimer);
    document.removeEventListener('visibilitychange', refreshIfDue);
    window.removeEventListener('online', refreshIfDue);
    feed?.unsubscribe();
  };
};
